import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import type { CompanyMetricSummary } from '@/features/actual-vs-budget/company-review'
import {
  formatPercentage,
  formatUsdMillions,
} from '@/features/actual-vs-budget/format'

interface SummaryCardProps {
  eyebrow: string
  value: string
  detail: string
}

function SummaryCard({ detail, eyebrow, value }: SummaryCardProps) {
  return (
    <Card className="rounded-md border-primary/10 bg-white shadow-none">
      <CardContent className="p-4">
        <Typography
          variant="body-sm"
          className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="metric-value"
          className="mt-2 text-xl font-semibold text-primary"
        >
          {value}
        </Typography>
        <Typography variant="muted" className="mt-1 text-xs leading-5">
          {detail}
        </Typography>
      </CardContent>
    </Card>
  )
}

export function CompanySummary({ summary }: { summary: CompanyMetricSummary }) {
  return (
    <section aria-labelledby="company-summary-heading" className="space-y-3">
      <div>
        <Typography
          id="company-summary-heading"
          as="h2"
          variant="h5"
          className="text-lg font-semibold"
        >
          Performance summary
        </Typography>
        <Typography variant="muted" className="mt-1">
          Matched fiscal periods through the latest closed month.
        </Typography>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Typography
            as="h3"
            variant="body-sm"
            className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary"
          >
            Fiscal year to date
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              eyebrow="FYTD Actual"
              value={formatUsdMillions(summary.fytdActual)}
              detail={
                summary.fytdActual === null
                  ? 'Matched Actuals unavailable'
                  : `Prior-year Actual ${formatUsdMillions(summary.fytdPriorYearActual)}`
              }
            />
            <SummaryCard
              eyebrow="FYTD Budget"
              value={formatUsdMillions(summary.fytdBudget)}
              detail={
                summary.fytdBudget === null
                  ? 'Budget data unavailable'
                  : 'Approved plan through the same closed period'
              }
            />
            <SummaryCard
              eyebrow="Variance vs Budget"
              value={formatPercentage(summary.fytdVariancePercent)}
              detail={formatUsdMillions(summary.fytdVarianceAmount)}
            />
            <SummaryCard
              eyebrow="Prior-year Actual"
              value={formatUsdMillions(summary.fytdPriorYearActual)}
              detail={
                summary.fytdPriorYearActual === null
                  ? 'Matched prior-year period unavailable'
                  : 'Same elapsed fiscal period last year'
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Typography
            as="h3"
            variant="body-sm"
            className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary"
          >
            Full year
          </Typography>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <SummaryCard
              eyebrow="Full-year Forecast"
              value={formatUsdMillions(summary.fullYearForecast)}
              detail={
                summary.fullYearForecast === null
                  ? 'Forecast unavailable'
                  : 'Fiscal year not complete'
              }
            />
            <SummaryCard
              eyebrow="Full-year Budget"
              value={formatUsdMillions(summary.fullYearBudget)}
              detail={
                summary.fullYearBudget === null
                  ? 'Budget data unavailable'
                  : 'Approved fiscal-year plan'
              }
            />
            <SummaryCard
              eyebrow="Forecast vs Budget"
              value={formatPercentage(summary.fullYearVariancePercent)}
              detail={
                summary.fullYearForecast === null
                  ? 'Forecast unavailable'
                  : summary.fullYearBudget === null
                    ? 'Budget data unavailable'
                    : `${formatUsdMillions(summary.fullYearVarianceAmount)} vs ${formatUsdMillions(summary.fullYearBudget)} Budget`
              }
            />
          </div>
        </div>
      </div>
    </section>
  )
}
